import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import Layout from '@/components/layout/Layout';
import { useTenant } from '@/contexts/TenantContext';
import { defaultArealForTenant, resolveAreal } from '@/pages/Teaching/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import DateInput from '@/components/ui/date-input';
import { PlzOrtBridge } from '@/components/address/PlzOrtBridge';

const EMPTY_FORM = {
  first_name: '',
  last_name: '',
  date_of_birth: '',
  gender: '',
  email: '',
  phone: '',
  address: '',
  postal_code: '',
  city: '',
  gkz: 'GRE',
  areal: 'NRW',
  status: 'active',
  short_note: '',
  notes: '',
  attention_source: '',
};

function toDateInput(value) {
  if (!value) return '';
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

export default function StudentEdit() {
  const { id } = useParams();
  const { activeTenant } = useTenant();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM,
    areal: defaultArealForTenant(activeTenant),
  }));

  useEffect(() => {
    if (isNew) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/students/${id}`);
        if (cancelled) return;
        const s = res.data;
        setForm({
          first_name: s.first_name || '',
          last_name: s.last_name || '',
          date_of_birth: toDateInput(s.date_of_birth),
          gender: s.gender || '',
          email: s.email || '',
          phone: s.phone || '',
          address: s.address || '',
          postal_code: s.postal_code || '',
          city: s.city || '',
          gkz: s.gkz || 'GRE',
          areal: resolveAreal(s.areal, activeTenant),
          status: s.status || 'active',
          short_note: s.short_note || '',
          notes: s.notes || '',
          attention_source: s.attention_source || '',
        });
      } catch {
        toast.error('Fehler beim Laden des Schülers');
        navigate('/students');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, isNew, navigate]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error('Vor- und Nachname sind Pflicht');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        date_of_birth: form.date_of_birth || null,
        gender: form.gender || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        address: form.address.trim() || null,
        postal_code: form.postal_code.trim() || null,
        city: form.city.trim() || null,
        gkz: form.gkz.trim() || null,
        areal: form.areal.trim() || null,
        status: form.status || 'active',
        short_note: form.short_note.trim() || null,
        notes: form.notes.trim() || null,
        attention_source: form.attention_source.trim() || null,
      };

      if (isNew) {
        const res = await api.post('/students', payload);
        toast.success('Schüler angelegt');
        navigate(`/students/${res.data.id}`);
      } else {
        await api.put(`/students/${id}`, payload);
        toast.success('Schüler gespeichert');
        navigate(`/students/${id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const fieldCls = 'h-9 text-sm';
  const selectCls = 'h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm';
  const backTo = isNew ? '/students' : `/students/${id}`;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-red" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-3xl" data-testid="student-edit-page">
        <button
          type="button"
          onClick={() => navigate(backTo)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"
          data-testid="student-edit-back"
        >
          <ArrowLeft className="w-4 h-4" />
          {isNew ? 'Zurück zur Liste' : 'Zurück zum Schüler'}
        </button>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h1 className="text-2xl font-bold text-slate-900 mb-1">
            {isNew ? 'Neuer Schüler' : 'Schüler bearbeiten'}
          </h1>
          {!isNew && (
            <p className="text-sm text-slate-500 mb-6">
              {form.first_name} {form.last_name}
            </p>
          )}
          {isNew && <div className="mb-6" />}

          <form onSubmit={handleSave} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="student-first-name">Vorname *</Label>
                <Input
                  id="student-first-name"
                  className={fieldCls}
                  value={form.first_name}
                  onChange={(e) => set('first_name', e.target.value)}
                  data-testid="student-edit-first-name"
                />
              </div>
              <div>
                <Label htmlFor="student-last-name">Nachname *</Label>
                <Input
                  id="student-last-name"
                  className={fieldCls}
                  value={form.last_name}
                  onChange={(e) => set('last_name', e.target.value)}
                  data-testid="student-edit-last-name"
                />
              </div>
              <div>
                <Label htmlFor="student-dob">Geburtsdatum</Label>
                <DateInput
                  id="student-dob"
                  className={fieldCls}
                  value={form.date_of_birth}
                  onChange={(e) => set('date_of_birth', e.target.value)}
                  data-testid="student-edit-dob"
                />
              </div>
              <div>
                <Label htmlFor="student-gender">Geschlecht</Label>
                <select
                  id="student-gender"
                  className={selectCls}
                  value={form.gender}
                  onChange={(e) => set('gender', e.target.value)}
                  data-testid="student-edit-gender"
                >
                  <option value="">—</option>
                  <option value="m">männlich</option>
                  <option value="w">weiblich</option>
                  <option value="d">divers</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="student-email">E-Mail</Label>
                <Input
                  id="student-email"
                  type="email"
                  className={fieldCls}
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                  data-testid="student-edit-email"
                />
              </div>
              <div>
                <Label htmlFor="student-phone">Telefon</Label>
                <Input
                  id="student-phone"
                  className={fieldCls}
                  value={form.phone}
                  onChange={(e) => set('phone', e.target.value)}
                  data-testid="student-edit-phone"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="student-address">Straße / Adresse</Label>
              <Input
                id="student-address"
                className={fieldCls}
                value={form.address}
                onChange={(e) => set('address', e.target.value)}
                data-testid="student-edit-address"
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="student-postal">PLZ</Label>
                <Input
                  id="student-postal"
                  className={fieldCls}
                  value={form.postal_code}
                  onChange={(e) => set('postal_code', e.target.value)}
                  data-testid="student-edit-postal"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="student-city">Ort</Label>
                <Input
                  id="student-city"
                  className={fieldCls}
                  value={form.city}
                  onChange={(e) => set('city', e.target.value)}
                  data-testid="student-edit-city"
                />
                <PlzOrtBridge
                  postalCode={form.postal_code}
                  city={form.city}
                  onCity={(value) => set('city', value)}
                  testId="student-edit-plz"
                />
              </div>
              <div>
                <Label htmlFor="student-status">Status</Label>
                <select
                  id="student-status"
                  className={selectCls}
                  value={form.status}
                  onChange={(e) => set('status', e.target.value)}
                  data-testid="student-edit-status"
                >
                  <option value="active">Aktiv</option>
                  <option value="inactive">Inaktiv</option>
                  <option value="paused">Pausiert</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="student-gkz">Filiale (GKZ)</Label>
                <Input
                  id="student-gkz"
                  className={fieldCls}
                  value={form.gkz}
                  onChange={(e) => set('gkz', e.target.value)}
                  data-testid="student-edit-gkz"
                />
              </div>
              <div>
                <Label htmlFor="student-areal">Region / Areal</Label>
                <Input
                  id="student-areal"
                  className={fieldCls}
                  value={form.areal}
                  onChange={(e) => set('areal', e.target.value)}
                  data-testid="student-edit-areal"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="student-short-note">Kurznotiz</Label>
              <Input
                id="student-short-note"
                className={fieldCls}
                value={form.short_note}
                onChange={(e) => set('short_note', e.target.value)}
                data-testid="student-edit-short-note"
              />
            </div>

            <div>
              <Label htmlFor="student-attention">Aufmerksam geworden durch</Label>
              <Input
                id="student-attention"
                className={fieldCls}
                value={form.attention_source}
                onChange={(e) => set('attention_source', e.target.value)}
                data-testid="student-edit-attention"
              />
            </div>

            <div>
              <Label htmlFor="student-notes">Notizen</Label>
              <textarea
                id="student-notes"
                rows={3}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
                data-testid="student-edit-notes"
              />
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(backTo)}
                data-testid="student-edit-cancel"
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-slate-900 text-white hover:bg-slate-800"
                data-testid="student-edit-save"
              >
                {saving ? 'Speichern…' : (isNew ? 'Anlegen' : 'Speichern')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}
